import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const MarcarAsistenciaEventoQR = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [evento, setEvento] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [marcando, setMarcando] = useState(false);

  const evento_id = searchParams.get('evento_id');

  useEffect(() => {
    // Verificar si el usuario está logueado
    if (!user) {
      // Guardar la URL actual para redirigir después del login
      sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
      navigate('/app');
      return;
    }

    // Verificar que haya evento_id en la URL
    if (!evento_id) {
      setError('No se especificó el evento. Por favor escanea el código QR nuevamente.');
      setLoading(false);
      return;
    }

    // Cargar información del evento
    loadEvento();
  }, [user, evento_id, navigate]);

  const loadEvento = async () => {
    try {
      const response = await axios.get(`${API}/eventos/${evento_id}`);
      setEvento(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading evento:', err);
      setError('Error al cargar la información del evento.');
      setLoading(false);
    }
  };

  const marcarAsistencia = async () => {
    if (!evento_id) return;

    setMarcando(true);
    try {
      const response = await axios.post(`${API}/eventos/marcar-asistencia-qr?evento_id=${evento_id}`);
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('¡Asistencia al evento marcada exitosamente!');
      }
    } catch (err) {
      console.error('Error marking attendance:', err);
      const errorMsg = err.response?.data?.detail || 'Error al marcar asistencia';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setMarcando(false);
    }
  };

  const formatDateTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, "d 'de' MMMM 'a las' HH:mm", { locale: es });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-purple-600 mb-4"></i>
            <p className="text-gray-600">Cargando información...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !evento) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-700 flex items-center justify-center">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-700">{error}</p>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/app/eventos')}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Volver a Eventos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-green-700 flex items-center justify-center">
              <i className="fas fa-check-circle text-3xl mr-2"></i>
              ¡Asistencia Confirmada!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Información del evento */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="text-center">
                <i className="fas fa-calendar-check text-3xl text-green-600 mb-3"></i>
                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                  {evento?.titulo}
                </h3>
                <p className="text-sm text-gray-600">
                  {evento?.tipo === 'online' ? 'Evento Online' : 'Evento Presencial'}
                </p>
              </div>
            </div>

            {/* Información de fecha */}
            {evento && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-center space-y-2">
                  <p className="text-sm text-gray-700">
                    <i className="fas fa-calendar-alt mr-1"></i>
                    {formatDateTime(evento.fecha_inicio)}
                  </p>
                </div>
              </div>
            )}

            {/* Mensaje de éxito */}
            <div className="bg-white p-4 rounded-lg border-2 border-green-300">
              <div className="text-center space-y-2">
                <p className="text-green-800 font-semibold">
                  Tu asistencia ha sido registrada correctamente
                </p>
                <p className="text-sm text-gray-600">
                  <i className="fas fa-clock mr-1"></i>
                  {new Date().toLocaleString('es-UY')}
                </p>
              </div>
            </div>

            {/* Información adicional */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 text-center">
                <i className="fas fa-info-circle mr-1"></i>
                Puedes cerrar esta ventana o volver a la página de eventos
              </p>
            </div>

            {/* Botones de acción */}
            <div className="space-y-2">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/app/eventos')}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Volver a Eventos
              </Button>
              
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => window.close()}
              >
                <i className="fas fa-times mr-2"></i>
                Cerrar esta ventana
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-purple-700 flex items-center justify-center">
            <i className="fas fa-qrcode text-2xl mr-2"></i>
            Marcar Asistencia a Evento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Información del evento */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-center">
              <i className="fas fa-calendar-check text-3xl text-purple-600 mb-3"></i>
              <h3 className="font-semibold text-gray-900 text-lg mb-1">
                {evento?.titulo}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {evento?.tipo === 'online' ? 'Evento Online' : 'Evento Presencial'}
              </p>
              <p className="text-sm text-gray-700">
                <i className="fas fa-calendar-alt mr-1"></i>
                {evento && formatDateTime(evento.fecha_inicio)}
              </p>
            </div>
          </div>

          {/* Ubicación o Link */}
          {evento?.tipo === 'presencial' && evento.ubicacion && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 text-center">
                <i className="fas fa-map-marker-alt mr-1"></i>
                <strong>Ubicación:</strong> {evento.ubicacion}
              </p>
            </div>
          )}

          {/* Información del usuario */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 text-center mb-2">
              <i className="fas fa-user mr-1"></i>
              Marcando asistencia como:
            </p>
            <p className="font-semibold text-gray-900 text-center">
              {user?.nombre} {user?.apellido}
            </p>
            <p className="text-xs text-gray-600 text-center">{user?.email}</p>
          </div>

          {/* Instrucciones */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-2 text-sm">
              <i className="fas fa-info-circle mr-1"></i>
              Importante:
            </h4>
            <ul className="text-xs text-yellow-700 space-y-1">
              <li>• Solo puedes marcar asistencia el día del evento</li>
              <li>• La marca es única (no se puede repetir)</li>
              <li>• Se registrará tu asistencia al evento</li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 text-center">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                {error}
              </p>
            </div>
          )}

          {/* Botón de marcar asistencia */}
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-semibold shadow-lg"
            onClick={marcarAsistencia}
            disabled={marcando}
          >
            {marcando ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Marcando asistencia...
              </>
            ) : (
              <>
                <i className="fas fa-check-circle mr-2"></i>
                Marcar mi asistencia
              </>
            )}
          </Button>

          {/* Botón cancelar */}
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => navigate('/app/eventos')}
          >
            <i className="fas fa-times mr-2"></i>
            Cancelar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarcarAsistenciaEventoQR;
